'use client';

import { SplitWizardData } from '@/widgets/registration-wizard/model/split-wizard-state';
import { forwardRef, useImperativeHandle, useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { BillingAddressFields } from '@/shared/ui/forms/billing-address-fields';
import {
    billingAddressSchema, type BillingAddressFormData,
} from '@/shared/lib/validation/registration-wizard/billing-address';


export type BillingAddressSubFormHandle = {
    /** Validates the form; on success commits via onSubmit and returns the data. Returns null on failure. */
    submit: () => Promise<BillingAddressFormData | null>;
    /** Returns current values without validation (for live Stripe billing_details). */
    getValues: () => BillingAddressFormData;
};


type Props = {
    onSubmit: (data: BillingAddressFormData) => void;
    onBillingEmailChange?: (email: string) => void;
    stateData: SplitWizardData
};


export const BillingAddressSubForm = forwardRef<BillingAddressSubFormHandle, Props>(
    function BillingAddressSubForm ({ onSubmit, onBillingEmailChange, stateData }, ref) {
        const form = useForm<BillingAddressFormData>({
            resolver      : zodResolver(billingAddressSchema),
            mode          : 'onBlur',
            reValidateMode: 'onChange',
            defaultValues : {
                billing_address         : stateData.company?.address ?? '',
                billing_address_extra   : '',
                billing_city            : stateData.company?.city ?? '',
                billing_country         : stateData.company?.country ?? '',
                billing_email           : stateData.email?.email ?? '',
                billing_email_additional: '',
                billing_postal_code     : stateData.company?.postal_code ?? '',
                vat_id                  : stateData.company?.company_vat ?? '',
                ...stateData.billing_address,
            },
        });

        useImperativeHandle(ref, () => ({
            submit   : async () => {
                const valid = await form.trigger();
                if (!valid) return null;
                const data = form.getValues();
                onSubmit(data);
                return data;
            },
            getValues: () => form.getValues(),
        }));

        return (
            <FormProvider { ...form }>
                <BillingAddressFields/>
                { onBillingEmailChange && (
                    <BillingEmailPusher control={ form.control } onChange={ onBillingEmailChange }/>
                ) }
            </FormProvider>
        );
    },
);


// Internal: subscribes to billingEmail and pushes to parent without re-rendering full form
const BillingEmailPusher = ({
    control,
    onChange,
}: {
    control: ReturnType<typeof useForm<BillingAddressFormData>>['control'];
    onChange: (email: string) => void;
}) => {
    const email = useWatch({ control, name: 'billing_email' });

    useEffect(() => {
        onChange(email ?? '');
    }, [ email, onChange ]);

    return null;
};
